// FILE PATH: eRFQFrontend/src/component/Vendor/LogisticDashboard/LogisticsRfqs.jsx

import React, { useState } from "react";
import Box from "@mui/material/Box";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SuccessModal from "../../../utils/SuccessModal";
import ErrorModal from "../../../utils/ErrorModal";

const LogisticsRfqs = ({ rfq, isLoading, activeTab, getRFQs, fetchCounts }) => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const vendorId = JSON.parse(localStorage.getItem("vendor"));

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleStatusUpdate = async (logisticDraftId, status) => {
    setLoading(true);
    setErrorMsg("");
    setOpenError(false);

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/logistic?vendorId=${vendorId}&logisticDraftId=${logisticDraftId}&status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );

      setOpen(true);
      setMsg(
        status === "INTERESTED"
          ? "Marked as Interested"
          : "Marked as Not Interested"
      );

      await getRFQs();
      await fetchCounts();
    } catch (error) {
      setErrorMsg(error?.response?.data || "Something went wrong");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const isWithin = (start, end) => {
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    const now = new Date();

    if (!startDate && !endDate) return true;
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    return true;
  };

  const statusColors = {
    INVITED: "#35D85B",
    INTERESTED: "#1DCCE8",
    NOT_INTERESTED: "#1DCCE8",
    EAUCTIONED: "#1DCCE8",
    BID_OPEN: "#35D85B",
    BID_SUBMITTED: "#35D85B",
    REBID: "#35D85B",
    BID_CLOSED: "rgb(190, 179, 179)",
    AWARDED: "#0381F3",
    DISQUALIFIED: "#FA6459",
  };

  const statusLabels = {
    INVITED: "INVITED",
    INTERESTED: "INTERESTED",
    NOT_INTERESTED: "NOT INTERESTED",
    EAUCTIONED: "EAUCTIONED",
    BID_OPEN: "RFQ OPEN",
    BID_SUBMITTED: "QUOTE SUBMITTED",
    REBID: "REVISE RFQ",
    BID_CLOSED: "RFQ CLOSED",
    AWARDED: "AWARDED",
    DISQUALIFIED: "DISQUALIFIED",
  };

  const columns = [
    {
      field: "logisticDraftId",
      headerName: "Logistics RFQ ID",
      flex: 1,
      renderCell: (params) => (
        <span
          className="table-title"
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(`/vendor/logistic-dashboard/${params.row.logisticDraftId}`)
          }
        >
          {params.row.logisticDraftId}
        </span>
      ),
    },
    {
      field: "route",
      headerName: "Origin -> Destination",
      flex: 1.2,
      renderCell: (params) => (
        <span>
          {`${params.row.origin} -> ${params.row.destination}`}
        </span>
      ),
    },
    {
      field: "modeOfConsignment",
      headerName: "Mode",
      flex: 0.8,
    },
    {
      field: "submissionDate",
      headerName: "Submission Date",
      flex: 1,
      renderCell: (params) => {
        const { rebidEndDate, submissionDate } = params.row;
        return formatDateTime(rebidEndDate || submissionDate);
      },
    },
    {
      field: "vrStatus",
      headerName: "Status",
      flex: 0.9,
      renderCell: (params) => (
        <span
          style={{
            background: statusColors[params.row.vrStatus],
            borderRadius: "15px",
            padding: "5px 15px",
            fontWeight: 600,
          }}
        >
          {statusLabels[params.row.vrStatus]}
        </span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1.8,
      sortable: false,
      renderCell: (params) => {
        const { vrStatus, logisticDraftId } = params.row;

        if (vrStatus === "INVITED") {
          return (
            <Box sx={{ display: "inline-flex", gap: 2 }}>
              <Button
                className="btn-contained"
                sx={{
                  margin: "0 !important",
                  fontSize: "12px",
                  width: "130px !important",
                }}
                onClick={() =>
                  handleStatusUpdate(logisticDraftId, "INTERESTED")
                }
              >
                Interested
              </Button>

              <Button
                className="btn-outlined btn-reject"
                sx={{
                  margin: "0 !important",
                  fontSize: "12px",
                  width: "130px !important",
                }}
                onClick={() =>
                  handleStatusUpdate(logisticDraftId, "NOT_INTERESTED")
                }
              >
                Not Interested
              </Button>
            </Box>
          );
        }

        if (vrStatus === "BID_OPEN" && params.row.draftStatus !== "BIDS_CLOSED") {
          return (
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() =>
                navigate(
                  `/vendor/logistic-dashboard/${logisticDraftId}/submit-quote`
                )
              }
            >
              Submit Quote
            </Button>
          );
        }

        if (
          vrStatus === "REBID" &&
          isWithin(params.row.rebidStartDate, params.row.rebidEndDate) &&
          params.row.draftStatus !== "BIDS_CLOSED"
        ) {
          return (
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() =>
                navigate(
                  `/vendor/logistic-dashboard/${logisticDraftId}/submit-rebid`
                )
              }
            >
              Submit Revised Quote
            </Button>
          );
        }

        if (vrStatus === "BID_SUBMITTED" || vrStatus === "AWARDED" || vrStatus === "DISQUALIFIED") {
          return (
            <Button
              className="btn-outlined"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() =>
                navigate(
                  `/vendor/logistic-dashboard/${logisticDraftId}/view-quote`
                )
              }
            >
              View Quote
            </Button>
          );
        }

        return null;
      },
    },
  ].filter(Boolean);

  return (
    <div style={{ position: "relative" }}>
      {loading && <div className="loading-div"></div>}

      <Box sx={{ background: "#fff" }}>
        <DataGrid
          rows={rfq || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.vrId}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          disableColumnResize
          slots={{ toolbar: CustomToolbar }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          sx={{
            position: "relative",
            border: "none",
            fontFamily: "Inter !important",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "700 !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              background: "none !important",
              color: "black",
              fontSize: "14px",
              borderRadius: "10px",
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#fff" },
            "& .MuiDataGrid-cell": { fontSize: "13px", border: "none" },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
              color: "#000 !important",
              border: "1px solid #B0B0B0",
              borderRadius: "12px",
              padding: 1,
            },
            "& .MuiInputBase-root-MuiInput-root::after": {
              borderBottom: "none !important",
            },
            "& .MuiInputBase-root-MuiInput-root": {
              borderBottom: "none !important",
            },
            [`& .${gridClasses.row}.even`]: { backgroundColor: "#EAF4FF" },
            "& .MuiInput-underline:before": {
              borderBottom: "none !important",
            },
            "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
              borderBottom: "none !important",
            },
          }}
        />
      </Box>

      <SuccessModal open={open} handleClose={() => setOpen(false)} msg={msg} />
      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default LogisticsRfqs;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end" }}>
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #B0B0B0",
          borderRadius: "12px",
          padding: "4px 12px",
          "&:hover": { backgroundColor: "#D8E9F0" },
          "& .MuiInputBase-root": { borderRadius: "12px" },
          width: 300,
        }}
      />
    </GridToolbarContainer>
  );
}

